import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchJson } from "@/lib/api";
import { EVENTS, track } from "@/lib/analytics";
import { useOrder } from "@/store/orderStore";

/**
 * Resolves a scanned table QR (`/r/:slug?tt=<signed token>`) into a locked table.
 *
 * The signature is never checked here — the secret is the server's. We ask the
 * backend what the code means and it answers with a short-lived **dine-in
 * session**, which is what we keep. The code from the card is deliberately
 * dropped: it is printed on a sticker that never changes, so storing it would
 * leave a permanent key to ordering in the phone, and the guest could order from
 * home tomorrow. The session expires by itself, and after that the storefront
 * asks for a fresh scan.
 *
 * The `tt` is stripped from the address bar either way, so a shared or bookmarked
 * link does not carry someone else's table with it.
 *
 * A rejected code is not fatal when the restaurant does not require scanning: the
 * guest falls back to typing a table number, which is all they could do before QR
 * codes existed.
 *
 * `qrError` is `{ status, detail }` — the refusal as the server described it,
 * left untranslated on purpose so the screen can render it in the language the
 * guest is reading right now.
 */
export function useQrTable(slug) {
  const { setGuest } = useOrder();
  const [searchParams, setSearchParams] = useSearchParams();
  const [qrError, setQrError] = useState(null);
  // True from the moment a `?tt=` is seen until the exchange finishes. The gate
  // in App.js waits on it, so a guest who has just scanned never sees "scan the
  // code at your table" flash by on their way in.
  const [scanning, setScanning] = useState(() => searchParams.has("tt"));
  const handled = useRef(false);

  useEffect(() => {
    const token = searchParams.get("tt");
    if (!token || handled.current) return;
    handled.current = true;

    (async () => {
      try {
        const res = await fetchJson(
          `/table-session?r=${encodeURIComponent(slug)}&tt=${encodeURIComponent(token)}`
        );
        setGuest({
          table: res.table ? String(res.table) : "",
          tableLocked: Boolean(res.table),
          tableToken: res.session_token,
          tableSessionExpiresAt: res.expires_at,
        });
        // The one arrival we can see. Everything upstream of it happened on a
        // sticker and a camera, where we have no instrumentation at all.
        track(EVENTS.QR_SCANNED, { result: "ok", has_table: Boolean(res.table) });
      } catch (e) {
        // The server's own sentence is kept, not a message: the screens render it
        // through `qrErrorMessage`, which turns the situation into the guest's
        // language and only falls back to these words when it does not recognise
        // them. Translating here would freeze the text in whatever language was
        // active at the moment the scan failed.
        setQrError({ status: e.status, detail: e.detail || null });
        setGuest({ tableLocked: false, tableToken: null, tableSessionExpiresAt: null });
        // The status, not the server's sentence: a refusal reason is a category
        // here, and the sentence is written for the guest reading it.
        track(EVENTS.QR_SCANNED, { result: "rejected", status: e.status || 0 });
      } finally {
        setScanning(false);
        const next = new URLSearchParams(searchParams);
        next.delete("tt");
        setSearchParams(next, { replace: true });
      }
    })();
  }, [slug, searchParams, setSearchParams, setGuest]);

  const dismissQrError = useCallback(() => setQrError(null), []);
  return { qrError, dismissQrError, scanning };
}
